// Honored Footer Service Utility
//
// NOTE: Footer configs below were pulled from production by manually inspecting footers in production environments.
// Usage: import and call HonoredFooterService.inject() after DOM is ready

declare global {
	interface Navigator {
		globalPrivacyControl?: boolean
	}
}

// Configuration for each brand's honored footer injection
interface HonoredFooterConfig {
	domain: string // Domain to match for this config
	tag: string // Tag to use for the injected element
	classes: string[] // CSS classes to apply
	linkFilter: (href: string, el?: Element) => boolean // Function to select the correct link(s)
	alignItems: string // CSS align-items value for parent
	multiInsert: boolean // Whether to insert after all matching links or just the first
	id: string // ID for the injected element
	text: string // Text content for the injected element
}

// Reusable class sets

// Enum for reusable footer CSS classes
enum FooterClass {
	Headline = 'gb-headline',
	Body1 = 'gb-body1',
	Body2 = 'gb-body2',
	Body3 = 'gb-body3',
	Headline6 = 'gb-headline6',
	Label = 'gb-label',
	ButtonLabel = 'gb-button-label',
	SmallMargin = 'gb-small-margin',
	Alt = 'alt',
	Invert = 'invert',
}

// Predefined class sets for each brand
const classSets = {
	buick: [FooterClass.Body2, FooterClass.Headline],
	cadillac: [FooterClass.Headline, FooterClass.Body3, FooterClass.Alt],
	chevrolet: [FooterClass.Headline, FooterClass.Body3],
	gmc: [FooterClass.Headline, FooterClass.Label, FooterClass.Invert],
	experience: [FooterClass.Headline, FooterClass.ButtonLabel, FooterClass.Invert],
	onstar: [FooterClass.Headline, FooterClass.Headline6],
	gmenergy: [FooterClass.Headline, FooterClass.Body3, FooterClass.Invert],
	gmcertified: [FooterClass.Body1, FooterClass.Invert],
	gmenvolve: [FooterClass.Headline, FooterClass.Body2, FooterClass.Invert],
	gmparts: [FooterClass.Headline, FooterClass.SmallMargin, FooterClass.Body1, FooterClass.Invert],
}

// Link filter functions to identify the correct anchor(s) in the footer
const adChoicesLinkFilter = (href: string) =>
	href.includes('/ad-choices') || href.includes('/adchoices') || href.includes('info.evidon.com')

const consumerPrivacyLinkFilter = (href: string, _el?: Element) => href.includes('consumer-privacy')
// Used when the same link appears multiple times in the footer: matches by href and text content
const linkAndTextFilter = (href: string, el?: Element) => {
	if (!href.includes('consumer-privacy')) return false
	if (!el) return false
	return el.textContent && el.textContent.trim() === 'Your Privacy Choices & Opt-Out Rights'
}

// Main configuration array for honored footer injection per brand
const honoredFooterConfig: HonoredFooterConfig[] = [
	{
		domain: 'buick.com',
		tag: 'h2',
		classes: classSets.buick,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-buick-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.buick,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'cadillac.com',
		tag: 'h2',
		classes: classSets.cadillac,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-cadillac-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.cadillac,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'chevrolet.com',
		tag: 'h2',
		classes: classSets.chevrolet,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-chevrolet-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.chevrolet,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmc.com',
		tag: 'h2',
		classes: classSets.gmc,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-gmc-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.gmc,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'experience.gm.com',
		tag: 'h3',
		classes: classSets.experience,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'experience-gm-com.stage.acp.gm.com',
		tag: 'h3',
		classes: classSets.experience,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'onstar.com',
		tag: 'h3',
		classes: classSets.onstar,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-onstar-com.stage.acp.gm.com',
		tag: 'h3',
		classes: classSets.onstar,
		linkFilter: linkAndTextFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmenergy.gm.com',
		tag: 'h3',
		classes: classSets.gmenergy,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmenergy-gm-com.stage.acp.gm.com',
		tag: 'h3',
		classes: classSets.gmenergy,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmcertified.com',
		tag: 'div',
		classes: classSets.gmcertified,
		linkFilter: adChoicesLinkFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-gmcertified-com.stage.acp.gm.com',
		tag: 'div',
		classes: classSets.gmcertified,
		linkFilter: adChoicesLinkFilter,
		alignItems: 'center',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmenvolve.com',
		tag: 'h2',
		classes: classSets.gmenvolve,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-gmenvolve-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.gmenvolve,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmfleet.com',
		tag: 'h2',
		classes: classSets.gmenvolve,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-gmfleet-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.gmenvolve,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: true,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'gmparts.com',
		tag: 'h2',
		classes: classSets.gmparts,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
	{
		domain: 'www-gmparts-com.stage.acp.gm.com',
		tag: 'h2',
		classes: classSets.gmparts,
		linkFilter: consumerPrivacyLinkFilter,
		alignItems: 'baseline',
		multiInsert: false,
		id: 'honored-footer-insert',
		text: 'GPC Honored',
	},
]

// Helper to create the honored footer element
function createHonoredFooterElement(cfg: HonoredFooterConfig): HTMLElement {
	const el = document.createElement(cfg.tag)
	el.textContent = cfg.text
	el.classList.add(...cfg.classes)
	el.id = cfg.id
	return el
}

// Service for injecting the honored footer element into the global footer if GPC is enabled
export const HonoredFooterService = {
	/**
	 * Injects the honored footer element after the appropriate link(s) in the global footer.
	 * Only runs if GPC (Global Privacy Control) is enabled in the browser.
	 * - Configs were pulled from production by inspecting live footers.
	 */
	inject(): void {
		if (window.parent?.navigator?.globalPrivacyControl !== true) {
			// Only inject if GPC is enabled
			return
		}
		console.log('HonoredFooterService: Checking for honored footer injection...')
		const currentHost = window.location.host.replace(/^www\./, '')
		// Find config for current domain
		const config = honoredFooterConfig.find(cfg => currentHost.endsWith(cfg.domain))
		if (!config) return
		// Find the global footer web component
		const footer = document.querySelector('gb-global-footer') as HTMLElement & { shadowRoot?: ShadowRoot }
		if (!footer || !footer.shadowRoot) return
		// Prevent duplicate injection
		if (footer.shadowRoot.getElementById(config.id)) return
		// Find all matching links in the footer
		const links = Array.from(footer.shadowRoot.querySelectorAll('a')).filter(a =>
			config.linkFilter((a as HTMLAnchorElement).href || '', a)
		)
		if (config.multiInsert) {
			// Insert after all matching links
			links.forEach(link => {
				if (link.nextElementSibling && link.nextElementSibling.id === config.id) return
				const el = createHonoredFooterElement(config)
				link.insertAdjacentElement('afterend', el)
				if (link.parentElement) {
					link.parentElement.style.display = 'flex'
					link.parentElement.style.gap = '1em'
					link.parentElement.style.alignItems = config.alignItems
				}
			})
		} else {
			// Insert after the first matching link only
			const adChoicesLink = links[0]
			if (adChoicesLink) {
				if (adChoicesLink.nextElementSibling && adChoicesLink.nextElementSibling.id === config.id) return
				const el = createHonoredFooterElement(config)
				adChoicesLink.insertAdjacentElement('afterend', el)
				if (adChoicesLink.parentElement) {
					adChoicesLink.parentElement.style.display = 'flex'
					adChoicesLink.parentElement.style.gap = '1em'
					adChoicesLink.parentElement.style.alignItems = config.alignItems
				}
			}
		}
	},
}
